import React, { Ref, useContext, useEffect, useState } from "react";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { ProductBuilderContext } from "../../../../contexts/ProductBuilderContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { getAction } from "../../../../services/siteflow";
import { getScreenClass } from "../../../../utils/screenClass";
import { FlexBox, useScreenClass } from "@vp/swan";
import classNames from "classnames";
import { useInView } from "react-intersection-observer";
import { bulkOrderBigQuantity, bulkOrderFlow } from "../../../../utils/bulkOrder";
import { SiteflowResponse } from "../../../../types/ContentfulTypes";
import {
  PlaceholderGroup,
  PlaceholderGroupDataType,
  PlaceholderGroupMember,
  PlaceholderGroupValidations,
} from "../../../../pluginsInfrastructure/PluginRegistryTypes";
import PluginPlaceholderGroup from "../../../../pluginsInfrastructure/PluginPlaceholderGroup";
import "../../styles/Ctas.scss";
// @ts-ignore
import { ensureUserTestId } from "@vp/ab-test-cookie";
import useGetStudioDocumentUrl from "../../../../hooks/useGetStudioDocumentUrl";
import { FeedTemplateContext } from "../../../../contexts/FeedTemplateProvider";
import { initCA } from "@vp/chat-anywhere";

export interface CtasContainerProps {
  customData: {
    showDescription: boolean;
  };
}

export interface CtaCustomData {
  flow: SiteflowResponse;
  isPrimaryCta: boolean;
  isLastCta: boolean;
  buttonIndex: number;
  showDescription: boolean;
  isDesignUploaded: boolean;
  hasMoreThanOneStudioFlow?: boolean;
}

const CtasContainer: React.FC<CtasContainerProps> = (props: CtasContainerProps) => {
  const [bulkOrderUrl, setBulkOrderUrl] = useState<string | undefined>();
  const { isMobile } = getScreenClass(useScreenClass());
  const { studioDocumentUrl } = useGetStudioDocumentUrl();
  const [caPlacement, setCaPlacement] = useState<string>();
  const placeholderGroupData: PlaceholderGroupDataType<CtaCustomData>[] = [];
  useEffect(() => {
    let mounted = true;
    const getBulkOrderUrl = async () => {
      const siteflowResponse = await getAction({
        tenant,
        culture: locale,
        productKey: mpv.coreProductId,
        version: mpv.version,
        flowAction: "bulkorder",
        optionValues: {},
        quantity: undefined,
        optimizelyEndUserId: ensureUserTestId(),
        isMobile,
        urlTokens: {
          documentUrl: studioDocumentUrl,
        },
        mpvId: mpv.mpvId,
      });
      if (mounted) {
        setBulkOrderUrl(siteflowResponse?.url);
      }
    };
    getBulkOrderUrl();
    return () => {
      mounted = false;
    };
  }, [isMobile]);

  const alwaysDisplayBorder = false;

  const { satisfactionGuaranteedInView } = useContext(ProductBuilderContext);
  const { multisiteflow } = useContext(BffDataContext);
  const { selectedQuantity } = useContext(ProductContext);
  const feedData = useContext(FeedTemplateContext);
  const { locale, mpv, ctaOverrideEntries, initialBuildState, tenant } = useContext(
    PageStaticDataContext
  );
  const buildStateCtas = initialBuildState?.siteflow;
  const [ctaRef, ctaInView] = useInView({
    threshold: 0,
  });

  const isBulkOrder = selectedQuantity === bulkOrderBigQuantity;
  let siteflow = isBulkOrder ? bulkOrderFlow(bulkOrderUrl!) : multisiteflow || buildStateCtas;

  if (feedData?.templateInformation?.studioUrl) {
    const flows: SiteflowResponse[] = [
      {
        type: "studio:loadedtemplate",
        url: feedData.templateInformation.studioUrl,
        context: [],
        requestUrl: "",
      },
    ];

    const galleryFlow = siteflow?.flows.find(flow => flow.type === "gallery");
    if (galleryFlow) {
      flows.push(galleryFlow);
    }
    siteflow = {
      flows,
    };
  }
  const hasMoreThanOneStudioFlow =
    (siteflow?.flows.filter(flow => flow.type.startsWith("studio")).length ?? 0) > 1;

  const ctaTypeLookup: Record<string, PlaceholderGroupMember> = {
    "99designs": PlaceholderGroupMember.CtaNinetyNineDesigns,
    addtocart: PlaceholderGroupMember.CtaAddToCart,
    albelligallery: PlaceholderGroupMember.CtaAlbelliGallery,
    albellistudio: PlaceholderGroupMember.CtaAlbelliStudio,
    bulkorder: PlaceholderGroupMember.CtaBulkOrder,
    gallery: PlaceholderGroupMember.CtaGallery,
    studio: PlaceholderGroupMember.CtaStudio,
    "studio:blank": PlaceholderGroupMember.CtaStudioBlank,
    "studio:custom": PlaceholderGroupMember.CtaStudioCustom,
    "studio:fullbleed": PlaceholderGroupMember.CtaStudioFullBleed,
    "studio:template": PlaceholderGroupMember.CtaStudioTemplate,
    uploadandgo: PlaceholderGroupMember.CtaUploadAndGo,
    uploadandprint: PlaceholderGroupMember.CtaUploadAndPrint,
    designassistant: PlaceholderGroupMember.CtaDesignAssistant,
    "studio:loadedtemplate": PlaceholderGroupMember.CtaEditTemplate,
  };

  const renderCtas = () => {
    let buttonIndex = 0;
    siteflow?.flows.forEach(flow => {
      const ctaOverride = ctaOverrideEntries?.[flow.type];
      if (ctaOverride?.disableCta) return;

      if (ctaTypeLookup[flow.type]) {
        const data: PlaceholderGroupDataType<CtaCustomData> = {
          type: ctaTypeLookup[flow.type],
          data: {
            flow,
            isPrimaryCta: buttonIndex === 0,
            isLastCta: buttonIndex === siteflow.flows.length - 1,
            buttonIndex,
            showDescription: props.customData.showDescription,
            isDesignUploaded: !!studioDocumentUrl,
            hasMoreThanOneStudioFlow,
          },
        };
        buttonIndex++;
        placeholderGroupData.push(data);
      }
    });

    return (
      <PluginPlaceholderGroup
        placeholderGroupType={PlaceholderGroup.CtaGroup}
        dataGroup={placeholderGroupData}
        validationTypes={[
          PlaceholderGroupValidations.AllPlaceholdersFilled,
          PlaceholderGroupValidations.AtLeastOnePlaceholderRendered,
        ]}
      />
    );
  };
  // Init chat on mount
  useEffect(() => {
    switch (placeholderGroupData.length) {
      case 1: {
        setCaPlacement("95px");
        break;
      }
      case 2: {
        setCaPlacement("150px");
        break;
      }
      case 3: {
        setCaPlacement("225px");
        break;
      }
    }
    initCA("", caPlacement);
  }, [placeholderGroupData]);
  return (
    <>
      <FlexBox
        className={classNames([
          "cta-group",
          { "cta-group-border": alwaysDisplayBorder || !satisfactionGuaranteedInView },
        ])}
        flexWrap="nowrap"
        justifyContent="space-between"
        marginTop={"between-subsections"}
        ref={ctaRef as Ref<HTMLDivElement>}
      >
        {renderCtas()}
      </FlexBox>
    </>
  );
};

export default CtasContainer;
